import React, { useState } from 'react';
import clsx from 'clsx';
import IconArrowDown from '@/assets/icons/icon-arrow-down-01.svg?react';
import IconSpinner from '@/assets/icons/icon-spiner.svg?react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectCurrentPage,
  selectPublishStatus,
  selectSelectedPath,
} from '@/store/pages/selectors';
import { selectSelectedDomain, selectSelectedDomainType } from '@/store/domains/selectors';
import { selectIsPublishingBlocked, selectUser } from '@/store/user/selectors';
import { publishPage, unpublishPage, fetchPublishedLandingPageCount } from '@/store/pages/thunks';
import { showToast } from '@/store/notifications/slice';
import { useParams } from 'react-router-dom';
import { useMixpanel } from '@/hooks/useMixpanel';
import { EVENT, PROPERTY } from '@/analytics/mixpanel';
import { convertToSlug } from '@/utils/slugHelper';
import { PublishControlPanel } from './PublishControlPanel';
import { Confetti, useConfetti } from './Confetti';
import { UpgradeToPublishModal } from '@/components/dialogs/UpgradeToPublishModal/UpgradeToPublishModal';

export const PublishControl: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector(selectCurrentPage);
  const publishingStatus = useAppSelector(selectPublishStatus);
  const isPublishingBlocked = useAppSelector(selectIsPublishingBlocked);
  const user = useAppSelector(selectUser);
  const selectedDomain = useAppSelector(selectSelectedDomain);
  const domainType = useAppSelector(selectSelectedDomainType);
  const selectedPath = useAppSelector(selectSelectedPath);
  const { uuid } = useParams<{ uuid: string }>();
  const track = useMixpanel();
  const confetti = useConfetti();

  const isPublished = !!currentPage?.publishedVersionId;
  const isDisabled = publishingStatus === 'publishing';

  // Mirror the thunk's fallback: use selectedPath if set, otherwise derive from title
  const effectivePath = selectedPath || (currentPage?.name ? convertToSlug(currentPage.name) : '');

  const domainHasChanged =
    isPublished && selectedDomain && currentPage?.domain && selectedDomain !== currentPage.domain;
  const pathHasChanged =
    isPublished && effectivePath && currentPage?.path && effectivePath !== currentPage.path;
  const urlChanged = domainHasChanged || pathHasChanged;

  // WHY: Closure over user, currentPage, and dispatch from component scope
  const isAtPageLimit = async (): Promise<boolean | null> => {
    const limit = user?.featureAccess?.leadpageLimit;
    if (limit === undefined || limit === null) return false;

    // If this page is already published, it's an update — not a new publish
    if (currentPage?.publishedVersionId) return false;

    try {
      const { total } = await dispatch(fetchPublishedLandingPageCount()).unwrap();
      return total >= limit;
    } catch {
      dispatch(
        showToast({
          text: 'Unable to verify your published page limit. Please try again.',
          state: 'error',
        }),
      );
      return null;
    }
  };

  const handleQuickPublish = async () => {
    if (isPublishingBlocked) {
      const mainAppUrl = import.meta.env.VITE_API_MAIN_APP;
      dispatch(
        showToast({
          text: 'Ready to publish? End your trial now to take your page live.',
          state: 'info',
          buttonPrimaryText: 'End Trial',
          buttonPrimaryHref: `${mainAppUrl}/my-account/billing?endTrialModal=1`,
        }),
      );
      return;
    }
    if (publishingStatus !== 'idle' || !uuid) return;

    const atPageLimit = await isAtPageLimit();
    if (atPageLimit === null) return;
    if (atPageLimit) {
      setShowUpgradeModal(true);
      return;
    }

    const domainProps = {
      [PROPERTY.DOMAIN]: selectedDomain ?? currentPage?.domain ?? '',
      [PROPERTY.DOMAIN_TYPE]: domainType,
    };

    const clickedEvent = !isPublished
      ? EVENT.PUBLISH_CLICKED
      : urlChanged
        ? EVENT.REPUBLISH_CLICKED
        : EVENT.UPDATE_CLICKED;
    const completedEvent = !isPublished
      ? EVENT.PUBLISH_COMPLETED
      : urlChanged
        ? EVENT.REPUBLISH_COMPLETED
        : EVENT.UPDATE_COMPLETED;
    const failedEvent = !isPublished
      ? EVENT.PUBLISH_FAILED
      : urlChanged
        ? EVENT.REPUBLISH_FAILED
        : EVENT.UPDATE_FAILED;

    track(clickedEvent, domainProps);

    const isFirstPublish = !isPublished;
    const isRepublish = isPublished && urlChanged;
    const startTime = Date.now();
    try {
      if (isRepublish) {
        await dispatch(unpublishPage({ pageId: uuid })).unwrap();
      }
      const result = await dispatch(publishPage({ pageId: uuid })).unwrap();
      const durationSeconds = Math.round((Date.now() - startTime) / 1000);
      track(completedEvent, { [PROPERTY.LOAD_TIME]: durationSeconds, ...domainProps });

      if (isFirstPublish || isRepublish) {
        const publishedUrl = `https://${result.page.domain}/${result.page.path}`;
        dispatch(
          showToast({
            text: 'Your page is now live!',
            state: 'success',
            buttonPrimaryText: 'View page',
            buttonPrimaryHref: publishedUrl,
          }),
        );
      } else {
        dispatch(
          showToast({
            text: 'Page updated!',
            state: 'success',
          }),
        );
      }

      if (isFirstPublish) {
        confetti.launch();
      }
    } catch (error) {
      let errorType = 'other';
      const errorMessage = typeof error === 'string' ? error : '';
      if (errorMessage.includes('No Page Name') || errorMessage.includes('validation')) {
        errorType = 'validation';
      } else if (errorMessage.includes('domain')) {
        errorType = 'domain';
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        errorType = 'network';
      }
      track(failedEvent, { [PROPERTY.ERROR_TYPE]: errorType, ...domainProps });
    }
  };

  const baseText = !isPublished ? 'Publish' : urlChanged ? 'Republish' : 'Update';
  const inProgressText = !isPublished
    ? 'Publishing...'
    : urlChanged
      ? 'Republishing...'
      : 'Updating...';
  const buttonText = publishingStatus === 'publishing' ? inProgressText : baseText;

  return (
    <div className="flex items-center">
      <Confetti pieces={confetti.pieces} />
      {/* Left: action button */}
      <button
        className={clsx(
          'flex items-center justify-center gap-1 rounded-l-md px-[11px] py-[7px] transition-all',
          {
            'cursor-progress bg-indigo-500': isDisabled,
            'cursor-pointer bg-indigo-500 hover:bg-indigo-700 focus-visible:bg-indigo-800 active:bg-indigo-800':
              !isDisabled,
          },
        )}
        onClick={handleQuickPublish}
        disabled={isDisabled}
      >
        {isDisabled && <IconSpinner className="h-5 w-5 animate-spin text-white" />}
        <span className="text-button-md text-white">{buttonText}</span>
      </button>

      {/* Divider */}
      <div className="h-[32px] w-px bg-indigo-200" />

      {/* Right: toggle arrow with dropdown */}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className={clsx(
              'flex items-center justify-center rounded-r-md px-1.5 py-[7px] transition-all',
              {
                'cursor-progress bg-indigo-500': isDisabled,
                'cursor-pointer bg-indigo-500 hover:bg-indigo-700 focus-visible:bg-indigo-800 active:bg-indigo-800':
                  !isDisabled,
              },
            )}
            disabled={isDisabled}
          >
            <IconArrowDown className="h-5 w-5 text-white" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-none bg-transparent p-0">
          <PublishControlPanel
            onClose={() => setOpen(false)}
            onPublish={handleQuickPublish}
            publishButtonText={buttonText}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <UpgradeToPublishModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </div>
  );
};
